'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { formatPrice } from '@/lib/formatters/currency.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { Maybe, WebDiscount } from '@gql-generated/graphql'
import parse from 'html-react-parser'
import type React from 'react'
import { ProductRowSource } from '../product-row/ProductRow.client'

export type ProductPriceDiscountsItemDiscount = {
  catalogDiscountAmount?: Maybe<number>
  webDiscount?: Maybe<WebDiscount>
}

type ProductPriceDiscountsProps = {
  hasDiscount: boolean
  cartItemDiscount?: ProductPriceDiscountsItemDiscount | null
  displaySavedAmount?: boolean
  source?: ProductRowSource
}

export function ProductPriceDiscounts({
  cartItemDiscount,
  hasDiscount,
  displaySavedAmount = true,
  source,
}: ProductPriceDiscountsProps): React.ReactNode {
  const { t } = useTranslation(['productPrice'])
  const webDiscount = cartItemDiscount?.webDiscount
  const shouldShowDiscountLabel =
    source === ProductRowSource.CART || source === ProductRowSource.ORDER_REQUEST

  if (!cartItemDiscount?.catalogDiscountAmount && !webDiscount?.label) {
    return null
  }

  return (
    <>
      {
        /// Saved amount on cart item
      }
      {!!cartItemDiscount?.catalogDiscountAmount && displaySavedAmount && (
        <p
          className={twMerge('mt-1 text-sm font-bold leading-lighter', hasDiscount && 'mt-0.5')}
          data-testid="product-price/discount-catalog"
        >
          {t('productPrice:savedAmount', {
            count: cartItemDiscount.catalogDiscountAmount,
          })}
        </p>
      )}

      {
        /// Web discount on cart item:
        /// - coupon if hasCoupon is true
        /// - universal otherwise
      }
      {webDiscount?.label &&
        typeof webDiscount.amount === 'number' &&
        typeof webDiscount.hasCoupon === 'boolean' && (
          <p
            className="text-sm font-bold leading-lighter desktop-md:text-right"
            data-testid="product-price/discount-web"
          >
            <span>{formatPrice(webDiscount.amount)}</span>{' '}
            {shouldShowDiscountLabel && (
              <>
                <span>
                  {webDiscount.hasCoupon
                    ? t('productPrice:discountLabel.code')
                    : t('productPrice:discountLabel.discount')}
                </span>{' '}
              </>
            )}
            <span>{parse(webDiscount.label)}</span>
          </p>
        )}
    </>
  )
}
